import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { 
  Building2, Users, FileText, LogOut, TrendingUp, 
  DollarSign, Activity, Menu, X, ChevronRight,
  Globe, Settings, BarChart3, BookOpen, ClipboardList, Brain, UserPlus,
  CreditCard, Rocket
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import SuperAdminClients from './SuperAdminClients';
import SuperAdminAnalytics from './SuperAdminAnalytics';
import SuperAdminKnowledgeBase from './SuperAdminKnowledgeBase';
import SuperAdminUsers from './SuperAdminUsers';
import SuperAdminReports from './SuperAdminReports';
import SuperAdminBehavioral from './SuperAdminBehavioral';
import SuperAdminInterventions from './SuperAdminInterventions';
import AdminSubscription from './AdminSubscription';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalUsers: 0,
    totalReports: 0,
    totalMRR: 0,
    activeTrials: 0,
  });

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate('/admin/login');
      return;
    }

    // Check for super_admin role
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .eq('role', 'super_admin')
      .maybeSingle();

    if (!roleData) {
      toast({
        title: "Access Denied",
        description: "You need super admin privileges to access this dashboard.",
        variant: "destructive",
      });
      navigate('/admin');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate('/admin/login');
  };

  const fetchStats = async () => {
    try {
      const [companiesRes, usersRes, reportsRes, settingsRes] = await Promise.all([
        supabase.from('companies').select('id', { count: 'exact', head: true }),
        supabase.from('worker_profiles').select('id', { count: 'exact', head: true }),
        supabase.from('peer_reports').select('id', { count: 'exact', head: true }),
        supabase.from('company_settings').select('mrr_arr, subscription_level'),
      ]);

      const totalMRR = settingsRes.data?.reduce((sum, s) => sum + (s.mrr_arr || 0), 0) || 0;
      const activeTrials = settingsRes.data?.filter(s => s.subscription_level === 'trial').length || 0;

      setStats({
        totalCompanies: companiesRes.count || 0,
        totalUsers: usersRes.count || 0,
        totalReports: reportsRes.count || 0,
        totalMRR,
        activeTrials,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'clients', label: 'Client Companies', icon: Building2 },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
    { id: 'users', label: 'All Users', icon: Users },
    { id: 'reports', label: 'All Reports', icon: FileText },
    { id: 'interventions', label: 'Intervention Analytics', icon: Rocket },
    { id: 'behavioral', label: 'Behavioral Data', icon: Brain },
    { id: 'analytics', label: 'Platform Analytics', icon: Activity },
    { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen },
    { id: 'experiments', label: 'Research Experiments', icon: ClipboardList },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewContent stats={stats} />;
      case 'clients':
        return <SuperAdminClients />;
      case 'subscriptions':
        return <AdminSubscription />;
      case 'users':
        return <SuperAdminUsers />;
      case 'reports':
        return <SuperAdminReports />;
      case 'interventions':
        return <SuperAdminInterventions />;
      case 'behavioral':
        return <SuperAdminBehavioral />;
      case 'analytics':
        return <SuperAdminAnalytics />;
      case 'knowledge':
        return <SuperAdminKnowledgeBase />;
      case 'experiments':
        return <SuperAdminInterventions />;
      default:
        return <OverviewContent stats={stats} />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col bg-card border-r transition-all duration-300",
        sidebarOpen ? "w-64" : "w-20"
      )}>
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-success flex items-center justify-center shrink-0">
              <img src="/src/assets/logo-symbol-white.svg" alt="SORIN" className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg">SORIN</h1>
                <p className="text-xs text-muted-foreground">Platform Admin</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left",
                activeTab === item.id 
                  ? "bg-primary text-primary-foreground" 
                  : "hover:bg-secondary text-muted-foreground hover:text-foreground"
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t space-y-2">
          <Button 
            variant="ghost" 
            className="w-full justify-start gap-3"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ChevronRight className={cn("h-5 w-5 transition-transform", !sidebarOpen && "rotate-180")} />
            {sidebarOpen && <span>Collapse</span>}
          </Button>
          <Button 
            variant="ghost" 
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50 lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-card z-50 lg:hidden shadow-2xl"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-success flex items-center justify-center">
                    <Globe className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <h1 className="font-bold">Platform Admin</h1>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setMobileSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="p-4 space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveTab(item.id); setMobileSidebarOpen(false); }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left",
                      activeTab === item.id 
                        ? "bg-primary text-primary-foreground" 
                        : "hover:bg-secondary text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </nav>
              <div className="p-4 border-t">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        <header className="sticky top-0 z-30 pt-safe bg-background/80 backdrop-blur-xl border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setMobileSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Platform Administration</h1>
                <p className="text-sm text-muted-foreground">Manage all SORIN client companies</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="hidden sm:flex">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

const OverviewContent = ({ stats }: { stats: any }) => {
  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    subtitle,
    variant = 'default' 
  }: { 
    title: string; 
    value: number | string; 
    icon: any; 
    subtitle?: string;
    variant?: 'default' | 'success' | 'warning';
  }) => (
    <Card className={cn(
      "border-0 shadow-sm hover:shadow-md transition-shadow",
      variant === 'success' && "bg-success/5 border-l-4 border-l-success",
      variant === 'warning' && "bg-warning/5 border-l-4 border-l-warning"
    )}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
            )}
          </div>
          <div className={cn(
            "p-3 rounded-2xl",
            variant === 'success' ? "bg-success/10" : 
            variant === 'warning' ? "bg-warning/10" : 
            "bg-primary/10"
          )}>
            <Icon className={cn(
              "h-6 w-6",
              variant === 'success' ? "text-success" :
              variant === 'warning' ? "text-warning" :
              "text-primary"
            )} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Companies" 
          value={stats.totalCompanies} 
          icon={Building2}
          subtitle="Active client companies"
        />
        <StatCard 
          title="Total Users" 
          value={stats.totalUsers} 
          icon={Users}
          subtitle="Across all companies"
        />
        <StatCard 
          title="Monthly Revenue" 
          value={`$${stats.totalMRR.toLocaleString()}`} 
          icon={DollarSign}
          variant="success"
        />
        <StatCard 
          title="Active Trials" 
          value={stats.activeTrials} 
          icon={TrendingUp}
          variant="warning"
          subtitle="Conversion opportunities"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest platform events</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm">Activity feed coming soon...</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full justify-start">
              <Building2 className="h-4 w-4 mr-2" />
              Add New Client Company
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Super Admin
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <FileText className="h-4 w-4 mr-2" />
              View All Reports
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Settings className="h-4 w-4 mr-2" />
              Platform Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;